import { useEffect, useState } from 'react';
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react';
import { ArrowRight, X } from 'lucide-react';

/** New Athena / platform destination — do not use VITE_PLATFORM_URL (that points at developer.jua.ai). */
export const NEW_PLATFORM_URL = 'https://platform.jua.ai';

const STORAGE_KEY = 'jua-legacy-migration-modal-seen';

function hasSeenMigrationModal(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

function markMigrationModalSeen(): void {
  try {
    localStorage.setItem(STORAGE_KEY, 'true');
  } catch {
    // Ignore storage failures (private mode, quota, etc.)
  }
}

export function MigrationModal() {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (!hasSeenMigrationModal()) {
      // Small delay so the page can settle before the modal appears
      const timeoutId = setTimeout(() => setIsOpen(true), 400);
      return () => clearTimeout(timeoutId);
    }
  }, []);

  const handleDismiss = () => {
    markMigrationModalSeen();
    setIsOpen(false);
  };

  const handleSwitch = () => {
    markMigrationModalSeen();
    window.open(NEW_PLATFORM_URL, '_blank', 'noopener,noreferrer');
    setIsOpen(false);
  };

  return (
    <Dialog open={isOpen} onClose={handleDismiss} className="relative z-[70]">
      <DialogBackdrop className="fixed inset-0 bg-black/40 backdrop-blur-sm" aria-hidden="true" />

      <div className="fixed inset-0 flex items-center justify-center p-4">
        <DialogPanel className="mx-auto w-full max-w-md rounded-xl bg-white dark:bg-gray-800 p-6 shadow-xl border border-gray-200 dark:border-gray-700">
          <div className="flex items-start justify-between gap-4 mb-4">
            <DialogTitle className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Switch to the new platform
            </DialogTitle>
            <button
              type="button"
              onClick={handleDismiss}
              className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              aria-label="Close"
              data-ph-capture-attribute="btn-migration-modal-close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed mb-6">
            You&apos;re viewing the legacy platform. Please switch to{' '}
            <span className="font-semibold text-gray-900 dark:text-gray-100">platform.jua.ai</span>{' '}
            before Friday, July 31.
          </p>

          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
            <button
              type="button"
              onClick={handleDismiss}
              className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
              data-ph-capture-attribute="btn-migration-modal-stay"
            >
              Stay on legacy
            </button>
            <button
              type="button"
              onClick={handleSwitch}
              className="inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-white bg-[#E34290] hover:bg-[#E34290]/90 rounded-lg transition-colors"
              data-ph-capture-attribute="btn-migration-modal-switch"
            >
              Go to platform.jua.ai
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
}
